import React, { Suspense } from 'react';
import { ConfigProvider } from 'antd';
import zhCN from 'antd/locale/zh_CN';
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import routes from './routes';
import adminRoutes from './routes/admin';
import store from './store';
import { Provider } from 'react-redux';
import './index.scss';
import VConsole from 'vconsole';
import { getUrlParams } from 'utils';
import { message } from 'antd';
import Maintenance from './pages/Maintenance';
import OrientationGuard from 'components/OrientationGuard';
import { isCapacitorIosNative } from 'utils/capacitorIosNative';
import VipGuard from 'components/VipGuard';
import PetBgm from 'components/PetBgm';
import HomeBgm from 'components/HomeBgm';
import UpdateNotice from 'components/UpdateNotice';
import { syncIosLockMainBounceRoute } from 'utils/capacitorIosOverscroll';
import { initAppAudioLifecycle } from 'utils/appAudioLifecycle';

// 配置 antd message，限制最大 DOM 数量，防止内存泄漏
message.config({
  maxCount: 1, // 最多同时显示 1 条消息
  duration: 2, // 2秒后自动关闭
});

// 维护模式配置
const MAINTENANCE_MODE = false; // 设置为 false 可关闭维护模式
const MAINTENANCE_END_TIME = new Date('2026-01-20T20:00:00').getTime();

// 动态设置 html 的 font-size  
function setRemUnit() {
  // 设计稿宽度  
  const designWidth = 440;
  // 基础字体大小，1rem 对应的 px 值  
  const baseFontSize = 10;
  // 获取当前屏幕宽度  
  const screenWidth = window.innerWidth;
  // 计算并设置 html 的 font-size  
  const remSize = (screenWidth / designWidth) * baseFontSize;

  document.documentElement.style.fontSize = `${remSize}px`;
}

// 初始化时调用  
setRemUnit();

// 监听窗口大小变化事件  
window.addEventListener('resize', setRemUnit);
const env = process.env.NODE_ENV;
const urlParams = getUrlParams(window.location.href);
console.log(urlParams,'urlParams')
if (urlParams?.env === 'test') {
  new VConsole();
}

// 页面可见性监听组件（需要在Router内部使用useNavigate）
const AppContent = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // 全局网络状态监听（处理DNS解析失败等问题）
  React.useEffect(() => {
    const handleOnline = () => {
      console.log('网络已连接');
      message.success('网络连接已恢复', 2);
    };
    
    const handleOffline = () => {
      console.warn('网络已断开');
      message.warning('网络连接已断开，请检查网络设置', 3);
    };
    
    // 监听网络状态变化
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    // 初始检查网络状态
    if (!navigator.onLine) {
      message.warning('当前网络不可用，请检查网络设置', 3);
    }
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // iOS 15+ 修复：如果访问 /admin 但首屏是 index.html（manifest 是用户端的），强制刷新获取 admin.html
  React.useEffect(() => {
    const currentPath = location.pathname;
    if (currentPath.startsWith('/admin')) {
      // 检查是否已经尝试过刷新（避免服务器配置错误时的无限循环）
      const refreshKey = 'admin-html-refresh-attempted';
      const hasAttemptedRefresh = sessionStorage.getItem(refreshKey);
      
      // 检查首屏 HTML 的 manifest（iOS 15+ 读取首屏，不是动态更新的）
      const initialManifest = document.querySelector('link[rel="manifest"]');
      const isInitialAdminManifest = initialManifest?.getAttribute('href')?.includes('manifest-admin.json');
      
      // 如果首屏 manifest 不是 admin 的，说明是从用户端跳转过来的，需要刷新获取 admin.html
      if (!isInitialAdminManifest && !hasAttemptedRefresh) {
        console.log('检测到 /admin 路径但首屏是用户端 HTML，强制刷新以获取 admin.html（iOS 15+ 需要）');
        sessionStorage.setItem(refreshKey, 'true');
        // 使用 replace 避免历史记录堆积
        window.location.replace(window.location.href);
        return; // 刷新后这个 effect 不会继续执行
      } else if (isInitialAdminManifest) {
        // 如果已经是 admin manifest，清除标记（可能用户直接访问 /admin）
        sessionStorage.removeItem(refreshKey);
      }
    }
  }, [location.pathname]);

  // iOS 壳：仅 Home/Detail 锁橡皮筋，其它页不锁（与 BridgeViewController 根滚动恢复配合）
  React.useEffect(() => {
    syncIosLockMainBounceRoute(location.pathname);
  }, [location.pathname]);

  // 后台暂停 BGM / 挨饿等音频，回前台按标记恢复（见 HomeBgm、PetBgm、soundEffect）
  React.useEffect(() => {
    initAppAudioLifecycle();
  }, []);

  // 根据路径动态切换 manifest 和图标（用于PWA保存到桌面）
  React.useEffect(() => {
    const updateManifestAndIcons = () => {
      const currentPath = location.pathname;
      const isAdminPage = currentPath.startsWith('/admin');
      
      // 移除现有的 manifest link
      const existingManifest = document.querySelector('link[rel="manifest"]');
      if (existingManifest) {
        existingManifest.remove();
      }

      // 根据路径添加对应的 manifest
      const manifestLink = document.createElement('link');
      manifestLink.rel = 'manifest';
      manifestLink.href = isAdminPage ? '/manifest-admin.json' : '/manifest.json';
      document.head.appendChild(manifestLink);

      // 更新 iOS apple-touch-icon
      const appleIcons = document.querySelectorAll('link[rel="apple-touch-icon"]');
      appleIcons.forEach(icon => icon.remove());
      
      if (isAdminPage) {
        // 后台图标 - 使用存在的图标文件（优先使用 512，浏览器会自动缩放）
        const adminIconLink = document.createElement('link');
        adminIconLink.rel = 'apple-touch-icon';
        adminIconLink.href = '/admin-icon-512.png';
        document.head.appendChild(adminIconLink);
        
        // 添加不同尺寸的图标（如果文件存在，浏览器会选择最合适的）
        const sizes = ['180', '152', '120'];
        sizes.forEach(size => {
          const link = document.createElement('link');
          link.rel = 'apple-touch-icon';
          link.setAttribute('sizes', `${size}x${size}`);
          link.href = `/admin-icon-${size}.png`;
          document.head.appendChild(link);
        });
      } else {
        // 用户端图标（恢复默认）
        const link1 = document.createElement('link');
        link1.rel = 'apple-touch-icon';
        link1.href = '/apple-touch-icon.png';
        document.head.appendChild(link1);
        
        const link2 = document.createElement('link');
        link2.rel = 'apple-touch-icon';
        link2.setAttribute('sizes', '180x180');
        link2.href = '/apple-touch-icon.png';
        document.head.appendChild(link2);
        
        const link3 = document.createElement('link');
        link3.rel = 'apple-touch-icon';
        link3.setAttribute('sizes', '152x152');
        link3.href = '/apple-touch-icon-152.png';
        document.head.appendChild(link3);
        
        const link4 = document.createElement('link');
        link4.rel = 'apple-touch-icon';
        link4.setAttribute('sizes', '120x120');
        link4.href = '/apple-touch-icon-120.png';
        document.head.appendChild(link4);
      }

      // 更新 iOS meta 标签
      let appleTitle = document.querySelector('meta[name="apple-mobile-web-app-title"]');
      if (appleTitle) {
        appleTitle.setAttribute('content', isAdminPage ? '悟空后台' : '悟空听说');
      } else {
        const meta = document.createElement('meta');
        meta.name = 'apple-mobile-web-app-title';
        meta.content = isAdminPage ? '悟空后台' : '悟空听说';
        document.head.appendChild(meta);
      }

      // 更新主题颜色
      let themeColor = document.querySelector('meta[name="theme-color"]');
      if (themeColor) {
        themeColor.setAttribute('content', isAdminPage ? '#667eea' : '#FFBB00');
      } else {
        const meta = document.createElement('meta');
        meta.name = 'theme-color';
        meta.content = isAdminPage ? '#667eea' : '#FFBB00';
        document.head.appendChild(meta);
      }

      // 更新 favicon（浏览器标签页图标）
      const favicons = document.querySelectorAll('link[rel="icon"]');
      favicons.forEach(icon => icon.remove());
      
      if (isAdminPage) {
        // 后台 favicon - 使用 512 尺寸作为 favicon（浏览器会自动缩放）
        const faviconLink = document.createElement('link');
        faviconLink.rel = 'icon';
        faviconLink.type = 'image/png';
        faviconLink.href = '/admin-icon-512.png';
        document.head.appendChild(faviconLink);
        
        // 32x32 尺寸的 favicon
        const favicon32Link = document.createElement('link');
        favicon32Link.rel = 'icon';
        favicon32Link.type = 'image/png';
        favicon32Link.sizes = '32x32';
        favicon32Link.href = '/admin-icon-512.png'; // 使用 512 作为备用
        document.head.appendChild(favicon32Link);
      } else {
        // 用户端 favicon（恢复默认）
        const faviconLink = document.createElement('link');
        faviconLink.rel = 'icon';
        faviconLink.href = '/favicon.ico';
        document.head.appendChild(faviconLink);
        
        const favicon32Link = document.createElement('link');
        favicon32Link.rel = 'icon';
        favicon32Link.type = 'image/png';
        favicon32Link.sizes = '32x32';
        favicon32Link.href = '/favicon-32x32.png';
        document.head.appendChild(favicon32Link);
      }

      // 更新页面标题
      document.title = isAdminPage ? '悟空后台 - 合伙人后台' : '悟空听说';
    };

    // 初始更新
    updateManifestAndIcons();
  }, [location.pathname]);

  // 页面可见性监听：当用户离开 app 后回来时自动回 home（H5 / Android 等）；iOS 真机壳不启用，避免打断当前页
  React.useEffect(() => {
    if (isCapacitorIosNative()) {
      return undefined;
    }

    let wasHidden = false;

    const handleVisibilityChange = () => {
      if (document.hidden || document.visibilityState === 'hidden') {
        // 页面被隐藏（用户离开app）
        wasHidden = true;
        console.log('页面已隐藏，用户离开app');
      } else if (wasHidden && (document.visibilityState === 'visible' || !document.hidden)) {
        // 页面变为可见（用户回到app）
        // 测试环境不自动跳转，便于调试
        if (env === 'development') {
          wasHidden = false;
          return;
        }
        const currentPath = location.pathname;
        const currentSearchParams = new URLSearchParams(location.search || '');
        // demo 彩蛋：serial_id=222 的 detail 返回时不强制离开
        const isDemo222Detail =
          currentPath === '/detail' &&
          currentSearchParams.get('demo') === '1' &&
          (currentSearchParams.get('serial_id') === '222' || currentSearchParams.get('demo_serial_id') === '222');
        
        console.log('页面已显示，用户回到app', { currentPath });
        
        // 排除不需要跳转的页面（登录、注册、忘记密码、下载、编辑页面等）
        const excludePaths = [
          '/',
          '/login',
          '/register',
          '/forgotPassword',
          '/download',
          '/about',
          '/welcome', // 首次登录欢迎页
          '/personCenterEdit', // 编辑页面（上传头像时会打开系统选择器）
          '/checkin',
          '/honors',
          '/admin/login',
          '/admin/register'
        ];
        
        // 判断是否是后台管理页面
        const isAdminPage = currentPath.startsWith('/admin');
        // 后台系统不自动跳转，保持在当前页面
        if (isAdminPage) {
          wasHidden = false;
          return;
        }
        // 商城模块不自动跳转
        if (currentPath.startsWith('/mall')) {
          wasHidden = false;
          return;
        }
        
        // 如果当前在排除列表中，不跳转
        if (excludePaths.includes(currentPath) || currentPath.startsWith('/about')) {
          wasHidden = false;
          return;
        }

        if (isDemo222Detail) {
          wasHidden = false;
          return;
        }
        
        // 用户端页面：进过 welcome 但未完成 s111 时，切回强制去 welcome；否则去 home
        if (!isAdminPage && currentPath !== '/home') {
          try {
            if (localStorage.getItem('need_assessment_pending') === '1') {
              console.log('自动跳转到 welcome（未完成测评）');
              navigate('/welcome', { replace: true });
            } else {
              console.log('自动跳转到用户端home页面');
              navigate('/home', { replace: true });
            }
          } catch (e) {
            navigate('/home', { replace: true });
          }
        }
        
        wasHidden = false;
      }
    };
    
    // 监听页面可见性变化
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // 兼容iOS WebView：监听页面焦点事件
    const handleFocus = () => {
      // 延迟检查，确保页面已完全激活
      setTimeout(() => {
        if (wasHidden) {
          // 测试环境不自动跳转，便于调试
          if (env === 'development') {
            wasHidden = false;
            return;
          }
          const currentPath = location.pathname;
          const currentSearchParams = new URLSearchParams(location.search || '');
          // demo 彩蛋：serial_id=222 的 detail 返回时不强制离开
          const isDemo222Detail =
            currentPath === '/detail' &&
            currentSearchParams.get('demo') === '1' &&
            (currentSearchParams.get('serial_id') === '222' || currentSearchParams.get('demo_serial_id') === '222');
          const excludePaths = [
            '/',
            '/login',
            '/register',
            '/forgotPassword',
            '/download',
            '/about',
            '/welcome',
            '/personCenterEdit', // 编辑页面（上传头像时会打开系统选择器）
            '/admin/login',
            '/admin/register'
          ];
          
          // 判断是否是后台管理页面
          const isAdminPage = currentPath.startsWith('/admin');
          // 后台系统不自动跳转，保持在当前页面
          if (isAdminPage) {
            wasHidden = false;
            return;
          }
          // 商城模块不自动跳转
          if (currentPath.startsWith('/mall')) {
            wasHidden = false;
            return;
          }
          
          // 如果当前在排除列表中，不跳转
          if (excludePaths.includes(currentPath) || currentPath.startsWith('/about')) {
            wasHidden = false;
            return;
          }

          if (isDemo222Detail) {
            wasHidden = false;
            return;
          }
          
          // 用户端页面：进过 welcome 但未完成 s111 时，切回强制去 welcome；否则去 home
          if (!isAdminPage && currentPath !== '/home') {
            try {
              if (localStorage.getItem('need_assessment_pending') === '1') {
                console.log('自动跳转到 welcome（未完成测评，focus事件）');
                navigate('/welcome', { replace: true });
              } else {
                console.log('自动跳转到用户端home页面（focus事件）');
                navigate('/home', { replace: true });
              }
            } catch (e) {
              navigate('/home', { replace: true });
            }
          }
          
          wasHidden = false;
        }
      }, 100);
    };
    
    const handleBlur = () => {
      wasHidden = true;
      console.log('页面失去焦点，用户离开app');
    };
    
    window.addEventListener('focus', handleFocus);
    window.addEventListener('blur', handleBlur);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('blur', handleBlur);
    };
  }, [navigate, location.pathname, location.search]);

  // 渲染路由的函数
  const renderRoutes = (routesList, prefix = '', wrapSuspense = false) => {
    return routesList.map((route) => {
      const path = prefix ? `${prefix}/${route.path}` : route.path;
      const element = wrapSuspense ? (
        <Suspense fallback={null}>{route.element}</Suspense>
      ) : (
        route.element
      );
      
      // 如果有子路由，使用嵌套路由
      if (route.children && route.children.length > 0) {
        return (
          <Route key={path} path={path} element={element}>
            {route.children.map((child, idx) => (
              <Route 
                key={child.index ? `${path}/index-${idx}` : `${path}/${child.path || 'child'}-${idx}`}
                index={child.index || false}
                path={child.index ? undefined : (child.path || undefined)}
                element={wrapSuspense ? <Suspense fallback={null}>{child.element}</Suspense> : child.element}
              />
            ))}
          </Route>
        );
      }
      
      return (
        <Route key={path} path={path} element={element} />
      );
    });
  };

  const isAdminPage = location.pathname.startsWith('/admin');
  // 登录、注册、忘记密码、个人中心编辑等也做横屏限制；/about 企业介绍页在 PC 端打开供审核，不做重力/横屏限制
  // iOS 真机：系统对横竖屏与 WebView 视口处理与 H5 不一致，关闭「请竖屏使用」全屏遮罩（仍可在 Xcode 里锁竖屏若需要）
  const orientationGuardEnabled =
    !isAdminPage && !location.pathname.startsWith('/about') && !isCapacitorIosNative();

  return (
    <>
      {/* 全局更新通知：看一次即缓存，由 UPDATE_NOTICE_SEEN_KEY 控制 */}
      <UpdateNotice />
      {/* Home/Menu 背景音乐：home、menu 共用 main.mp3，切换不中断 */}
      <HomeBgm />
      {/* Pet 区域背景音乐：pet / 食物商城 / 大圣图鉴 / 每日任务 共用一曲，切换不中断 */}
      <PetBgm />
      {/* 强制竖屏：用户端生效（后台不限制）；输入框聚焦时跳过判断，避免键盘导致误判 */}
      <OrientationGuard enabled={orientationGuardEnabled} />
      <VipGuard>
        <Routes>
          {/* C端（用户端）路由 */}
          {routes.map((route) => (
            <Route key={route.path} path={route.path} element={route.element} />
          ))}
          {/* B端（后台管理）路由 */}
          {renderRoutes(adminRoutes, 'admin', true)}
        </Routes>
      </VipGuard>
    </>
  );
};

const App = () => {
  // 判断是否在维护期间
  const isInMaintenance = MAINTENANCE_MODE && Date.now() < MAINTENANCE_END_TIME;

  // 维护期间显示维护页面
  if (isInMaintenance) {
    return <Maintenance />;
  }

  return (
    <Provider store={store}>
      <ConfigProvider locale={zhCN}>
        <Router>
          <AppContent />
        </Router>
      </ConfigProvider>
    </Provider>
  );
};

export default App;